import { Box, Link } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import React, { ReactElement } from 'react'

import { useAnalytics } from '../../lib/analytics'
import { Actions, Categories } from '../../lib/analytics/events'
import { Theme } from '../../lib/theming/Theme'
import { Text } from '../utils/typography'

declare global {
  interface Window {
    CookieControl: {
      open: () => void
    }
  }
}

const useStyles = makeStyles<Theme>(theme => ({
  bottom: {
    borderTop: `1px solid ${theme.palette.common.white}`,
    display: 'flex',
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.8rem',
    justifyContent: 'space-between',
    marginTop: '3.1rem',
    paddingLeft: '1.2rem',
    paddingRight: '1.2rem',
    paddingTop: '0.9rem',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  bottomGroup: {
    [theme.breakpoints.up('md')]: {
      '& :not(:last-child)::after': {
        content: "'|'",
        marginLeft: '1em',
        marginRight: '1em',
      },
    },
    display: 'flex',
  },
  bottomLeft: {
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  bottomLink: {
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0.5rem',
    },
  },
  bottomMiddle: {
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      order: 3,
    },
  },
  bottomRight: {
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  container: {
    '& a': {
      '&:hover': {
        textDecoration: 'none',
      },
      color: theme.palette.common.white,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    },
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: '960px',
    paddingBottom: '4rem',
    paddingTop: '4rem',
    position: 'relative',
    zIndex: 1,
  },
  groupLink: {
    '&:hover': {
      paddingLeft: '0.8rem',
    },
    paddingBottom: '0.5rem',
    transition: 'padding-left .4s ease',
  },
  groups: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    justifyContent: 'center',
  },
  linkGroupHeader: {
    color: theme.palette.common.white,
    fontFamily: theme.typography.fontFamily,
    fontSize: '1.2rem',
    paddingBottom: '0.5rem',
    paddingTop: '1rem',
    textDecoration: 'underline',
    [theme.breakpoints.down('sm')]: {
      textDecoration: 'none',
    },
  },
  linksGroup: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '1.2rem',
    width: 'calc(100% - 2.4rem)',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      width: 'auto',
    },
  },
  root: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  spanLink: {
    cursor: 'pointer',
  },
}))

export type LinkProps = {
  href?: string
  name: string
}

export type LinksGroupProps = {
  items: LinkProps[]
  name: string
}

const LinksGroup = ({ name, items }: LinksGroupProps) => {
  const classes = useStyles()

  return (
    <div className={classes.linksGroup}>
      <div className={classes.linkGroupHeader}>
        <span>{name}</span>
      </div>
      {items.map(item => (
        <LinkGroupItem key={item.name} href={item.href} name={item.name} />
      ))}
    </div>
  )
}

const LinkGroupItem = ({ name, href }: LinkProps) => {
  const classes = useStyles()
  const { trackEvent } = useAnalytics()

  const handleClick = () => {
    trackEvent({
      action: Actions.MenuItemClick,
      category: Categories.FooterMenu,
      label: name,
      location: Categories.FooterMenu,
    })
  }

  return (
    <div className={classes.groupLink}>
      <Link href={href} onClick={handleClick}>
        <Text color="inherit">{name}</Text>
      </Link>
    </div>
  )
}

const BottomLink = (props: LinkProps) => {
  const { name, href } = props
  const classes = useStyles()
  const { trackEvent } = useAnalytics()

  const handleClick = () => {
    trackEvent({
      action: Actions.MenuItemClick,
      category: Categories.FooterMenu,
      label: name,
      location: Categories.FooterMenu,
    })
  }

  return (
    <Box className={classes.bottomLink}>
      {href ? (
        <Link href={href} onClick={handleClick}>
          {name}
        </Link>
      ) : (
        <span>{name}</span>
      )}
    </Box>
  )
}

type BottomLinksProps = {
  left: LinkProps[]
  middle: LinkProps[]
  right: LinkProps[]
}

const BottomLinks = ({ left, middle, right }: BottomLinksProps) => {
  const classes = useStyles()

  return (
    <Box className={classes.bottom}>
      <Box className={clsx(classes.bottomGroup, classes.bottomLeft)}>
        {left.map(link => (
          <BottomLink key={link.name} href={link.href} name={link.name} />
        ))}
      </Box>
      <Box className={clsx(classes.bottomGroup, classes.bottomMiddle)}>
        {middle.map(link => (
          <BottomLink key={link.name} href={link.href} name={link.name} />
        ))}
      </Box>
      <Box className={clsx(classes.bottomGroup, classes.bottomRight)}>
        {right.map(link => (
          <BottomLink key={link.name} href={link.href} name={link.name} />
        ))}
        <span
          aria-hidden="true"
          className={classes.spanLink}
          onClick={() => window.CookieControl.open()}
        >
          <BottomLink name="Cookies" />
        </span>
      </Box>
    </Box>
  )
}

type FooterProps = {
  bottomLinks: BottomLinksProps
  children?: ReactElement
  linkGroups: LinksGroupProps[]
}

const Footer = ({ children, linkGroups, bottomLinks }: FooterProps) => {
  const classes = useStyles()

  return (
    <div className={classes.root}>
      <Box className={classes.container}>
        <Box className={classes.groups}>
          {linkGroups.map(group => (
            <LinksGroup key={group.name} items={group.items} name={group.name} />
          ))}
        </Box>
        <BottomLinks
          left={bottomLinks.left}
          middle={bottomLinks.middle}
          right={bottomLinks.right}
        />
      </Box>
      {children && children}
    </div>
  )
}

export default Footer
